import type { XlsxAnchor } from '../../types/xlsxAnchor';
import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/index';
import { LAYOUT } from '../../constants/layout';

export type SidebarMode = 'mini' | 'expanded' | 'hidden';

type NotificationBody =
  | { messageKey: string; message?: never }
  | { message: string; messageKey?: never };

/**
 * Exactly one of messageKey / message — a notification with no text is a blank
 * toast. Declared without `id` first: `Omit` takes only a union's SHARED keys,
 * so omitting from the full type would collapse the discriminant.
 */
export type NotificationInput = {
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  category?: 'session' | 'general';
} & NotificationBody;

export type Notification = NotificationInput & { id: string };

/**
 * Which panel is floating over the page, if any. Only one can sensibly cover a
 * narrow screen at a time, so this is a single slot rather than a flag per
 * panel. Deliberately not persisted: a remembered "open" would bury the page
 * behind an overlay on first paint.
 */
export type OverlayPanel = 'nav' | 'chat' | null;

interface UIState {
  xlsxContext: XlsxAnchor | null;
  sidebarMode: SidebarMode;
  overlayPanel: OverlayPanel;
  chatOpen: boolean;
  chatWidth: number | null;  // null = use responsive default
  theme: 'light' | 'dark';
  notifications: Notification[];
  modals: {
    uploadDocument: boolean;
    documentDetails: boolean;
    shareDocument: boolean;
    userProfile: boolean;
  };
}

const getInitialSidebarMode = (): SidebarMode => {
  const stored = localStorage.getItem('sidebarMode');
  if (stored === 'mini' || stored === 'expanded' || stored === 'hidden') return stored;
  return 'mini';
};

const getInitialChatOpen = (): boolean => {
  const stored = localStorage.getItem('chatOpen');
  if (stored !== null) return stored === 'true';
  return true;
};

const getInitialChatWidth = (): number | null => {
  const stored = localStorage.getItem('chatWidth');
  if (stored) {
    const n = parseInt(stored, 10);
    if (n >= 280 && n <= 800) return n;
  }
  return null;
};

const initialState: UIState = {
  xlsxContext: null,
  sidebarMode: getInitialSidebarMode(),
  overlayPanel: null,
  chatOpen: getInitialChatOpen(),
  chatWidth: getInitialChatWidth(),
  theme: localStorage.getItem('theme') as 'light' | 'dark' || 'light',
  notifications: [],
  modals: {
    uploadDocument: false,
    documentDetails: false,
    shareDocument: false,
    userProfile: false,
  },
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setXlsxContext: (state, action: PayloadAction<XlsxAnchor | null>) => { state.xlsxContext = action.payload; },
    // Legacy actions — rewired to sidebarMode for backward compatibility
    toggleSidebar: (state) => {
      const next = state.sidebarMode === 'expanded' ? 'mini' : 'expanded';
      state.sidebarMode = next;
      localStorage.setItem('sidebarMode', next);
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      const next = action.payload ? 'expanded' : 'mini';
      state.sidebarMode = next;
      localStorage.setItem('sidebarMode', next);
    },
    setSidebarMode: (state, action: PayloadAction<SidebarMode>) => {
      state.sidebarMode = action.payload;
      localStorage.setItem('sidebarMode', action.payload);
    },
    toggleSidebarExpand: (state) => {
      const next = state.sidebarMode === 'expanded' ? 'mini' : 'expanded';
      state.sidebarMode = next;
      localStorage.setItem('sidebarMode', next);
    },
    setOverlayPanel: (state, action: PayloadAction<OverlayPanel>) => {
      state.overlayPanel = action.payload;
    },
    toggleOverlayPanel: (state, action: PayloadAction<Exclude<OverlayPanel, null>>) => {
      state.overlayPanel = state.overlayPanel === action.payload ? null : action.payload;
    },
    /**
     * Also drives the mobile slot so callers outside the shell (VerificationPage
     * opens the panel to show results) work on a phone, where visibility reads
     * `overlayPanel` rather than the persisted flag.
     */
    setChatOpen: (state, action: PayloadAction<boolean>) => {
      state.chatOpen = action.payload;
      state.overlayPanel = action.payload ? 'chat' : null;
      localStorage.setItem('chatOpen', String(action.payload));
    },
    toggleChat: (state) => {
      state.chatOpen = !state.chatOpen;
      localStorage.setItem('chatOpen', String(state.chatOpen));
    },
    setChatWidth: (state, action: PayloadAction<number>) => {
      const w = Math.max(280, Math.min(800, action.payload));
      state.chatWidth = w;
      localStorage.setItem('chatWidth', String(w));
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },
    showNotification: (state, action: PayloadAction<NotificationInput>) => {
      state.notifications.push({
        ...action.payload,
        id: nanoid(),
        duration: action.payload.duration || 5000,
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    clearSessionNotifications: (state) => {
      state.notifications = state.notifications.filter(n => n.category !== 'session');
    },
    openModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = false;
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(key => {
        state.modals[key as keyof UIState['modals']] = false;
      });
    },
  },
});

// Actions
export const {
  setXlsxContext,
  toggleSidebar,
  setSidebarOpen,
  setSidebarMode,
  toggleSidebarExpand,
  setOverlayPanel,
  toggleOverlayPanel,
  setChatOpen,
  setChatWidth,
  toggleChat,
  setTheme,
  showNotification,
  removeNotification,
  clearNotifications,
  clearSessionNotifications,
  openModal,
  closeModal,
  closeAllModals,
} = uiSlice.actions;

// Selectors
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarMode === 'expanded';
export const selectSidebarMode = (state: RootState) => state.ui.sidebarMode;
export const selectOverlayPanel = (state: RootState) => state.ui.overlayPanel;
export const selectSidebarWidth = (state: RootState) => {
  switch (state.ui.sidebarMode) {
    case 'expanded': return LAYOUT.SIDEBAR_WIDTH_EXPANDED;
    case 'mini': return LAYOUT.SIDEBAR_WIDTH_MINI;
    case 'hidden': return 0;
  }
};
export const selectChatOpen = (state: RootState) => state.ui.chatOpen;
export const selectChatWidth = (state: RootState) => state.ui.chatWidth;
export const selectTheme = (state: RootState) => state.ui.theme;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectModals = (state: RootState) => state.ui.modals;

export default uiSlice.reducer;
